import { Link, useSearchParams } from "react-router-dom";
import { Check, Triangle, X, ArrowUp, ArrowDown, ArrowUpRight, MapPin, ChevronDown, PawPrint, BarChart3, Inbox, AlertOctagon, RotateCw } from "lucide-react";
import "./deepcoast.css";

export const useV = () => {
  const [sp] = useSearchParams();
  const v = sp.get("v");
  return ["1", "2", "3"].includes(v) ? v : "3";
};

const VersionSwitch = () => {
  const v = useV();
  const [, setSp] = useSearchParams();
  return (
    <div className="tabs" data-testid="dc-version-switch">
      {["1", "2", "3"].map((x) => <button key={x} className={`tab ${v === x ? "active" : ""}`} onClick={() => setSp({ v: x })} data-testid={`dc-version-${x}`}>V{x}</button>)}
    </div>
  );
};

export const STATUS = {
  pos: { Icon: Check, Arrow: ArrowUp, text: "On Track" },
  warn: { Icon: Triangle, Arrow: ArrowUpRight, text: "At Risk" },
  neg: { Icon: X, Arrow: ArrowDown, text: "Behind" },
};
export const statusOf = (pct) => (pct >= 80 ? "pos" : pct >= 60 ? "warn" : "neg");
export const money = (v) => `$${Math.round(v).toLocaleString()}`;

export const Badge = ({ s, children, testid }) => {
  const { Icon } = STATUS[s];
  return <span className={`badge ${s}`} data-testid={testid}><Icon size={11} strokeWidth={3} />{children || STATUS[s].text}</span>;
};

export const Shell = ({ title, children, testid }) => {
  const v = useV();
  return (
  <div className={`dc v${v}`} data-testid={testid}>
    <header style={{ borderBottom: "1px solid var(--divider)", position: "sticky", top: 0, background: "rgba(255,255,255,0.86)", backdropFilter: "blur(16px)", zIndex: 20 }}>
      <div style={{ maxWidth: 1440, margin: "0 auto", padding: "10px 24px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <Link to={`/mockups?v=${v}`} style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none", color: "var(--text)" }} data-testid="dc-home-link">
          <span style={{ width: 28, height: 28, borderRadius: 8, background: "var(--accent-ink)", display: "grid", placeItems: "center" }}><PawPrint size={16} color="#fff" /></span>
          <b style={{ fontSize: 14 }}>FourPawIQ</b>
        </Link>
        <nav style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {[["Daily Ops", "/mockups/renewals"], ["Leads", "/mockups/leads"], ["Exec 2.0", "/mockups/exec"]].map(([l, to]) => (
            <Link key={to} to={`${to}?v=${v}`} className={`tab ${title === l ? "active" : ""}`} style={{ textDecoration: "none" }} data-testid={`dc-nav-${l.replace(/\W/g, "").toLowerCase()}`}>{l}</Link>
          ))}
        </nav>
        <button className="btn" style={{ marginLeft: "auto" }} data-testid="dc-location-select"><MapPin size={14} color="var(--accent-ink)" />K9 Resorts of Chalfont<ChevronDown size={14} /></button>
        <VersionSwitch />
      </div>
    </header>
    <main style={{ maxWidth: 1440, margin: "0 auto", padding: "24px" }}>{children}</main>
  </div>
  );
};

export const PageHead = ({ title, sub, right }) => (
  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 16, marginBottom: 24 }} className="enter">
    <div style={{ flex: "1 1 320px" }}>
      <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.1, margin: 0 }}>{title}</h1>
      {sub && <p className="muted" style={{ margin: "6px 0 0", fontSize: 14 }}>{sub}</p>}
    </div>
    {right && <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>{right}</div>}
  </div>
);

export const Sparkline = ({ data, color = "var(--accent-ink)" }) => {
  const max = Math.max(...data), min = Math.min(...data);
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * 100},${28 - ((v - min) / (max - min || 1)) * 24}`).join(" ");
  return <svg viewBox="0 0 100 30" preserveAspectRatio="none" style={{ width: "100%", height: 28 }}><polyline className="draw" points={pts} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" /></svg>;
};

export const Kpi = ({ label, value, sub, status, trend, spark, delay = 0, testid }) => {
  const v = useV();
  const ink = status && !(v === "3" && status === "pos") ? `var(--${status}-ink)` : "var(--text)";
  const St = status && STATUS[status];
  return (
    <div className="card enter" style={{ padding: 16, animationDelay: `${delay}ms`, display: "flex", flexDirection: "column", gap: 6 }} data-testid={testid}>
      <div className="label">{label}</div>
      <div className="mono" style={{ fontSize: "var(--kpi-size)", fontWeight: 600, color: ink, lineHeight: 1.05, letterSpacing: "-0.02em" }}>{value}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minHeight: 20 }}>
        {St && <Badge s={status} />}
        {trend && <span className="mono" style={{ fontSize: 11, color: St ? ink : "var(--muted)", display: "inline-flex", alignItems: "center", gap: 2 }}>{St && <St.Arrow size={12} />}{trend}</span>}
        {sub && <span className="muted" style={{ fontSize: 11 }}>{sub}</span>}
      </div>
      {spark && <Sparkline data={spark} color={ink === "var(--text)" ? "var(--accent-ink)" : ink} />}
    </div>
  );
};

export const Progress = ({ label, value, goal, fmt = money, testid }) => {
  const pct = Math.round((value / goal) * 100), s = statusOf(pct), target = 100 / 1.2;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid={testid}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span className="label">{label}</span>
        <span className="mono" style={{ fontSize: 12 }}><b>{fmt(value)}</b> <span className="muted">/ {fmt(goal)}</span> <span style={{ color: `var(--${s}-ink)`, fontWeight: 700 }}>{pct}%</span></span>
      </div>
      <div style={{ position: "relative", height: 8, background: "var(--track)", borderRadius: 4 }}>
        <div className="grow-x" style={{ width: `${Math.min(pct / 1.2, 100)}%`, height: "100%", borderRadius: 4, background: `linear-gradient(90deg, color-mix(in srgb, var(--${s}) 40%, transparent), var(--${s}))` }} />
        <div style={{ position: "absolute", left: `${target}%`, top: -4, bottom: -4, width: 2, background: "var(--text)" }} />
        <span className="mono" style={{ position: "absolute", left: `${target}%`, top: 12, transform: "translateX(-50%)", fontSize: 10, color: "var(--muted)" }}>Target</span>
      </div>
      <div style={{ height: 10 }} />
    </div>
  );
};

export const ChartTip = ({ active, payload, fmt }) => (active && payload?.length ? (
  <div className="tt">{payload.map((p) => <div key={p.dataKey}><b>{fmt ? fmt(p.value) : p.value}</b><span>{p.payload.tip || p.name}{p.name && p.payload.tip ? ` · ${p.name}` : ""}</span></div>)}</div>
) : null);

export const ICON_TONES = {
  teal: ["#E6F6F3", "#1F8A7C"], blue: ["#EFF6FF", "#2563C9"], purple: ["#F5F3FF", "#6D4BD8"], amber: ["#FFF7E6", "#B45309"],
  rose: ["#FFF1F2", "#C8304B"], green: ["#F0FBF5", "#1E8A55"], slate: ["#F1F5F7", "#4B5D66"],
};

export const Section = ({ title, sub, icon, tone = "teal", right, children, delay = 0, testid, style }) => {
  const v = useV();
  const Icon = v === "1" ? null : icon;
  const [bg, fg] = ICON_TONES[v === "3" ? "teal" : tone];
  if (v === "1") sub = null;
  return (
    <section className="card enter" style={{ padding: 20, animationDelay: `${delay}ms`, ...style }} data-testid={testid}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flex: "1 1 260px", minWidth: 0 }}>
          {Icon && <span className="sec-icon" style={{ background: bg }}><Icon size={18} color={fg} strokeWidth={2.25} /></span>}
          <div style={{ minWidth: 0 }}><h2 className={v === "1" ? "chart-title" : "sec-title"}>{title}</h2>{sub && <div className="muted" style={{ fontSize: 12 }}>{sub}</div>}</div>
        </div>
        <div style={{ marginLeft: "auto" }}>{right}</div>
      </div>
      {children}
    </section>
  );
};

export const Hero = ({ s, label, value, sub, icon: Icon, delay = 0, testid }) => {
  const v = useV();
  if (v === "1") return <Kpi label={label.replace(/^[✗△✓]\s*/, "").replace(/ · [✗△✓].*$/, "")} value={value} status={s === "accent" ? null : s} trend={sub} delay={delay} testid={testid} />;
  if (v === "3") return (
    <div className={`soft ${s} enter`} style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
      <span className="soft-label">{label}</span>
      <span className="mono" style={{ fontSize: "var(--kpi-size)", fontWeight: 700, lineHeight: 1.15, color: `var(--${s === "accent" ? "accent" : s}-ink)` }}>{value}</span>
      {sub && <span className="muted" style={{ fontSize: 12 }}>{sub}</span>}
      {Icon && <Icon className="soft-icon" size={16} color={`var(--${s === "accent" ? "accent" : s}-ink)`} />}
    </div>
  );
  return (
    <div className={`hero ${s} enter`} style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
      <span className="hero-label">{label}</span>
      <span className="mono" style={{ fontSize: 40, fontWeight: 700, lineHeight: 1.05, letterSpacing: "-0.02em" }}>{value}</span>
      {sub && <span style={{ fontSize: 12, opacity: 0.92 }}>{sub}</span>}
      {Icon && <span className="hero-icon"><Icon size={20} color="#fff" /></span>}
    </div>
  );
};

export const Tint = ({ c, label, value, sub, icon: Icon, delay = 0, testid }) => {
  const v = useV();
  if (v !== "2") return (
    <div className="plain enter" style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
      <span className="plain-label">{Icon && v === "3" && <Icon size={13} color="var(--faint)" />}{label}</span>
      <span className="mono" style={{ fontSize: v === "3" ? 24 : 32, fontWeight: 600, lineHeight: 1.15 }}>{value}</span>
      {sub && <span className="muted" style={{ fontSize: 12 }}>{sub}</span>}
    </div>
  );
  return (
    <div className={`tint ${c} enter`} style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
      <span className="tint-label">{Icon && <Icon size={13} strokeWidth={2.5} />}{label}</span>
      <span className="tint-val">{value}</span>
      {sub && <span className="muted" style={{ fontSize: 12 }}>{sub}</span>}
    </div>
  );
};

export const Alert = ({ tone = "", title, children, icon: Icon, testid }) => {
  const v = useV();
  if (v === "1") return null;
  const slim = v === "3";
  const ink = tone === "warn" ? (slim ? "var(--warn-ink)" : "#B45309") : (slim ? "var(--neg-ink)" : "#C8304B");
  return (
    <div className={`alert ${tone} ${slim ? "slim" : ""} enter`} data-testid={testid}>
      {Icon && <Icon size={slim ? 16 : 18} color={ink} style={{ marginTop: slim ? 0 : 1, flexShrink: 0 }} />}
      <div style={slim ? { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "baseline" } : undefined}><b>{title}</b><div className="muted" style={{ fontSize: 12 }}>{children}</div></div>
    </div>
  );
};

export const States = () => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }} data-testid="dc-states">
    <div className="card" style={{ padding: 24, textAlign: "center" }} data-testid="dc-state-empty"><BarChart3 size={28} color="var(--faint)" /><div style={{ fontWeight: 700, marginTop: 8 }}>No data available</div><div className="muted">No checkout customers need a package today.</div></div>
    <div className="card" style={{ padding: 24, display: "grid", gap: 10 }} data-testid="dc-state-loading"><div className="skeleton" style={{ height: 14, width: "40%" }} /><div className="skeleton" style={{ height: 32 }} /><div className="skeleton" style={{ height: 32 }} /><div className="skeleton" style={{ height: 32, width: "70%" }} /></div>
    <div className="card" style={{ padding: 24, textAlign: "center" }} data-testid="dc-state-error"><AlertOctagon size={28} color="var(--neg-ink)" /><div style={{ fontWeight: 700, marginTop: 8 }}>Unable to load</div><div className="muted" style={{ marginBottom: 12 }}>Gingr didn't respond in time.</div><button className="btn"><RotateCw size={13} />Retry</button></div>
  </div>
);

export const EmptyIcon = Inbox;
